import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import {
	SingleSignOnSettingsViewModel,
	SingleSignOnType,
	getSingleSignOnSettingsViewModel,
	ServiceHostingLocation
} from "client/Api/SingleSignOnSettings/singleSignOnSettings";
import { SettingsPage } from "../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import { SsoSettingsUpdate } from "./SsoSettingsUpdate/SsoSettingsUpdate";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { navigateRelativeToEnvironment, reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { SingleSignOnFormData, SingleSignOnSettingsControl } from "./SingleSignOnSettingsControl";
import { Form, ToolbarAction } from "client/Components";
import { convertToFormData, useSingleSignOnSettingsForm } from "./useSingleSignOnSettingsForm";
import { IFormHandler, useFormHandler } from "client/Forms";
import { ssoSettingsValidationConfiguration } from "./validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const SingleSignOnSettingsPage = (): ReactElement => {

	const isCustomSignOutRedirectUrlConfigurable: boolean = true;

	const [viewModel, setViewModel] = useState<SingleSignOnSettingsViewModel>(defaultViewModel);
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const [initialFormData, setInitialFormData] = useState<SingleSignOnFormData>(
		(): SingleSignOnFormData => {
			return convertToFormData(
				viewModel,
				isCustomSignOutRedirectUrlConfigurable
			);
		}
	);

	const [formData, setFormData] = useState<SingleSignOnFormData>(initialFormData);

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	useActiveSettingsMenuItem(SettingsPageKind.SingleSignOn);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const viewModel: SingleSignOnSettingsViewModel = await getSingleSignOnSettingsViewModel(signal)
						.catch(handlePageDataLoadingError);

					setViewModel(viewModel);
					const formData: SingleSignOnFormData = convertToFormData(viewModel, isCustomSignOutRedirectUrlConfigurable);

					setInitialFormData(formData);
					setFormData(formData);

					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			isCustomSignOutRedirectUrlConfigurable
		]
	);

	const formHandler: IFormHandler<SingleSignOnFormData> = useFormHandler(
		formData,
		setFormData,
		ssoSettingsValidationConfiguration
	);

	const [
		submitHandler,
		verifyOAuth2Handler,
		enableMicrosoftAuthenticatorMobileSignInHandler,
		hasUnsavedOAuth2CredentialsChanges,
		formDialog
	] = useSingleSignOnSettingsForm(
		formHandler,
		initialFormData,
		isBusy,
		setIsBusy
	);

	const doneHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<SingleSignOnType> = await submitHandler();

			if (!result.wasSuccessful) {
				return;
			}

			const selectedSingleSignOnType: SingleSignOnType = result.result;

			if (selectedSingleSignOnType === SingleSignOnType.OAuth2
				|| selectedSingleSignOnType === SingleSignOnType.WsFederation
			) {
				navigateRelativeToEnvironment("SignOut?skipSignOutFromExternalProvider=true");
			} else {
				reloadCurrentPage();
			}
		},
		[submitHandler]
	);

	const defaultActions = useMemo(
		(): readonly SsoAction[] => {
			return listDefaultActions({
				done: doneHandler
			});
		},
		[doneHandler]
	);

	const topBarActions = useMemo(
		(): readonly SsoAction[] => {
			return buildActionsForTopBar<undefined, SsoAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={undefined}
				title={localize("_SingleSignOn")}
			>
				<SsoSettingsUpdate
					requiresUpdate={viewModel.model.isUpdateRequired}
				/>

				<Form
					onSubmit={doneHandler}
					hasWidthConstrained
				>
					<SingleSignOnSettingsControl
						formHandler={formHandler}
						availableSingleSignOnTypes={viewModel.availableSingleSignOnTypes}
						signoutUrlHelpLink={viewModel.signOutHelpUrl}
						configurationHelpLink={viewModel.configurationHelpUrl}
						activeDirectoryHostingLocation={viewModel.activeDirectoryHostingLocation}
						hasCredentialsForCurrentUser={viewModel.hasCredentialsForCurrentUser}
						isAutomaticSetupAvailable={viewModel.isAutomaticSetupAvailable}
						productName={viewModel.productName}
						onVerifyOAuth2={verifyOAuth2Handler}
						onEnableMicrosoftAuthenticatorMobileSignIn={enableMicrosoftAuthenticatorMobileSignInHandler}
						hasUnsavedOAuth2CredentialsChanges={hasUnsavedOAuth2CredentialsChanges}
					/>
				</Form>

				<div className="preserve-space-for-mobile-plus-button" />
				<AiAssistantFabSpacing />
			</SettingsPage>

			{formDialog}
		</>
	);
};
SingleSignOnSettingsPage.displayName = "SingleSignOnSettingsPage";

type SsoAction = ToolbarAction<undefined>;

type DefaultActionsHandlers = {
	done: () => void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): readonly SsoAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			icon: "floppy-disk",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.done(),
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			icon: "xmark",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => reloadCurrentPage()
		}
	];
}

const defaultViewModel: SingleSignOnSettingsViewModel = {
	activeDirectoryHostingLocation: ServiceHostingLocation.Office365,
	availableSingleSignOnTypes: [],
	configurationHelpUrl: "",
	hasCredentialsForCurrentUser: false,
	isAutomaticSetupAvailable: false,
	productName: "",
	signOutHelpUrl: "",
	model: {
		isUpdateRequired: false,
		oAuth2ViewModel: {
			authority: "",
			clientId: "",
			domainHint: "",
			keyContainsValue: false,
			shouldSignOutWhenRefreshTokenExpires: false,
			isMobileAppMicrosoftAuthenticatorSignInEnabled: false
		},
		signOutRedirectUrl: "",
		singleSignOnType: null,
		wsFederationViewModel: {
			metadataDocumentUrl: ""
		}
	}
};

export const singleSignOnSettingsRouting = {
	page: "/SingleSignOnSettings"
};